import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {List} from 'immutable';
import MuiList from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import Link from '@material-ui/core/Link';
import ListItemText from '@material-ui/core/ListItemText';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import {makeStyles} from '@material-ui/core/styles';
import Button from '@components/button';
import Grid from '@material-ui/core/Grid';
import Text from '@components/text';
import {Job, JobId} from '@store/@types/kpi';
import confirm from 'utils/confirm';
import JobForm from './job-form';
import JobActions from '@store/job/JobActions';
import {GridNote} from '@components/grid/GridNote';
import {TrashIcon} from '@components/icon/TrashIcon';

interface JobListProps {
  fetchJobs: typeof JobActions.getJobsInAccount;
  jobs: List<Job>;
  deleteJob: (jobId: JobId) => Promise<void>;
}

export const JobList: React.FC<JobListProps> = ({deleteJob, fetchJobs, jobs}) => {
  useEffect(() => {
    fetchJobs().then().catch(console.error);
  }, [fetchJobs]);

  const [open, setOpen] = useState(false);
  const [editedJobId, setEditedJobId] = useState<string>('');
  const editedJob = useMemo<Job | undefined>(() => {
    if (editedJobId) {
      return jobs.find(el => el.id === editedJobId);
    }
  }, [editedJobId, jobs]);

  const createJob = useCallback(() => {
    setEditedJobId('');
    setOpen(true);
  }, [setEditedJobId, setOpen]);

  const editJob = useCallback(
    evt => {
      setEditedJobId(evt.currentTarget.id);
      setOpen(true);
    },
    [setEditedJobId, setOpen]
  );

  const closeJobModal = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const handleDeleteJob = useCallback(
    jobId => {
      const job = jobs.find(el => el.id === jobId);
      confirm(`Are you sure you want to delete the Job "${job?.name}"?`).then(() => {
        deleteJob(jobId).then().catch(console.error);
      }, console.error);
    },
    [deleteJob, jobs]
  );

  const {iconStyle, label, listWrap} = useStyles();
  return (
    <Grid justifyContent={'space-between'} container spacing={2}>
      <Grid item xs={12}>
        <Text variant="h1">Jobs</Text>
      </Grid>
      <Grid item xs={12}>
        <Button onClick={createJob} classes={{label}} variant="contained" color="primary">
          New Job
        </Button>
      </Grid>
      <Grid item xs={12} md={9} lg={6}>
        <div className={listWrap}>
          <MuiList dense={false} disablePadding>
            <ListItem key="-1">
              <ListItemText
                primary={
                  <Row>
                    <Text variant="h4" noWrap>
                      JOB
                    </Text>
                    <Text variant="h4" noWrap>
                      # OF EMPLOYEES
                    </Text>
                  </Row>
                }
              />
              <ListItemSecondaryAction />
            </ListItem>
            {jobs.map(({id, name, numUsers: c}) => (
              <ListItem button key={id} id={id} onClick={editJob}>
                <ListItemText
                  primary={
                    <Row>
                      <Text noWrap>
                        <Link>{name}</Link>
                      </Text>
                      <Text noWrap>{typeof c === 'number' ? c : '0'}</Text>
                    </Row>
                  }
                />
                <ListItemSecondaryAction>
                  <TrashIcon
                    tooltip="Delete"
                    className={iconStyle}
                    aria-label="delete"
                    onClick={() => handleDeleteJob(id)}
                  />
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </MuiList>
        </div>
      </Grid>
      <GridNote item md={3} xs={12} title={'How do Jobs work?'}>
        <p>
          Coach now supports assigning a job to your users (note that this is distinct from "role",
          which controls permission levels within the Coach application.)
        </p>
        <p>
          Jobs can be used when assigning KPIs; for example, you could create a job named " Sales
          Development Rep #1", assign it to users across departments and teams, and then assign the
          same KPI or goal to all users marked as having this job.
        </p>
      </GridNote>
      <JobForm open={open} job={editedJob} onClose={closeJobModal} />
    </Grid>
  );
};

const useStyles = makeStyles(theme => ({
  iconStyle: {
    height: 16,
    cursor: 'pointer',
  },
  label: {
    fontSize: 14,
    fontWeight: 700,
  },
  listWrap: {
    margin: theme.spacing(1, 0, 0, -2),
  },
}));

const Row: React.FC<{children: [JSX.Element, JSX.Element]}> = ({children}) => {
  return (
    <Grid container spacing={1}>
      <Grid zeroMinWidth item xs={8}>
        {children[0]}
      </Grid>
      <Grid item xs={4}>
        {children[1]}
      </Grid>
    </Grid>
  );
};
